import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel } from "@realtyflows/ui";
import { getOnboardingStatus, dismissOnboarding } from "../api";

/**
 * OnboardingChecklist — V1 post-signup activation flow.
 *
 * Renders a small checklist on the Dashboard with the agent's progress.
 * Each step links to the right page so the agent can complete it inline.
 *
 * Hidden when:
 *   • user has dismissed it (server-stored dismissal — sticky across devices)
 *   • all steps are complete (auto-hides on next load)
 */
export default function OnboardingChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    getOnboardingStatus()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) return null;
  if (data.dismissed || data.allComplete) return null;
  if (hiding) return null;

  const stepLink = {
    profile: "/settings",
    gmail: "/emails",
    first_client: "/clients",
    first_deal: "/deals/new",
    first_contract: "/contracts",
  };

  const handleDismiss = async () => {
    setHiding(true);
    dismissOnboarding().catch(() => {});
  };

  return (
    <Panel padding={20} style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>
            Welcome to RealtyFlows
          </div>
          <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>
            Finish setup to unlock the full AI experience. ({data.percentComplete}% done)
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Dismiss onboarding checklist"
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            fontSize: 12, color: t.textMuted, padding: "4px 8px", fontFamily: font.body,
          }}
        >
          Dismiss
        </button>
      </div>

      {/* Progress bar */}
      <div style={{ height: 6, background: t.border, borderRadius: 99, overflow: "hidden", marginBottom: 14 }}>
        <div style={{
          width: `${data.percentComplete}%`,
          height: "100%",
          background: t.emerald,
          transition: "width 0.4s ease",
        }} />
      </div>

      {/* Step rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {data.steps.map((step) => (
          <div
            key={step.id}
            onClick={() => !step.done && stepLink[step.id] && navigate(stepLink[step.id])}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 12px",
              background: step.done ? t.emerald + "10" : t.surfaceWarm,
              border: `1px solid ${step.done ? t.emerald + "30" : t.border}`,
              borderRadius: 8,
              cursor: step.done ? "default" : "pointer",
              opacity: step.done ? 0.85 : 1,
              transition: "background 0.15s ease",
            }}
          >
            {/* Checkmark / empty circle */}
            <div style={{
              width: 22, height: 22, borderRadius: 99,
              background: step.done ? t.emerald : "transparent",
              border: `2px solid ${step.done ? t.emerald : t.borderDark}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              {step.done && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body,
                textDecoration: step.done ? "line-through" : "none",
              }}>
                {step.label}
              </div>
              {!step.done && step.hint && (
                <div style={{ fontSize: 11, color: t.textMuted, marginTop: 1, fontFamily: font.body }}>
                  {step.hint}
                </div>
              )}
            </div>

            {!step.done && (
              <span style={{ fontSize: 11, color: t.emerald, fontFamily: font.body, fontWeight: 600 }}>
                Start →
              </span>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}
